import {Component, OnDestroy, OnInit} from '@angular/core';
import {FontAwesomeService} from '@services/font-awesome.service';
import {selectFooterAction, selectOrderCompletion, selectOrderCreation, selectUser} from '@store/reducers';
import {Store} from '@ngrx/store';
import {OrderCompletionState} from '@store/reducers/order-completion.reducer';
import {AccountService} from '@services/account.service';
import {ORDER_CREATION_CLEAR, OrderLoadFail, OrderLoadSuccess} from '@store/actions';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs/Subject';
import {ModalDataSet} from '@store/actions/modal.action';
import {ShipmentService} from '@services/shipment.service';
import {PaymentService} from '@services/payment.service';
import {OrderCreationService, ShipmentPaymentStatusElement} from '@services/order-creation.service';
import {Shipment} from '@models/shipment.interface';
import {ShipmentUpdate} from '@store/actions/order-creation.action';
import {OrderCreationState} from '@store/reducers/order-creation.reducer';
import {DisableInvoice, OrderCompletionClear} from '@store/actions/order-complition.action';
import {GoogleAnalyticsEventsService} from '@services/google-analytics-events.service';
import {Router} from '@angular/router';
import {FooterActionEnum} from '@models/footer-action.enum';
import {PrintDocumentsService} from '@services/print-documents.service';
import {PrintFormatEnum, PrintTypeEnum} from '@models/print.enum';
import {BrowserPrintService} from '@services/browser-print.service';
import {User} from '@models/user.interface';
import {TranslateService} from '@ngx-translate/core';
import {MatSnackBar} from '@angular/material';
import {SnackBarComponent} from '@components/snackbar/snackbar.component';
import {environment} from '../../../../environments/environment';
import 'rxjs/add/operator/map';

@Component({
  selector: 'app-order-complete',
  templateUrl: './order-complete.component.html',
  styleUrls: ['./order-complete.component.scss']
})
export class OrderCompleteComponent implements OnInit, OnDestroy {
  orderCompletion: OrderCompletionState;
  private unsubscribe: Subject<void> = new Subject();
  shipments: Shipment[];
  private orderId = null;
  private user: User;
  private userPrintSetting;
  private userPrintFormat;

  constructor(
    public faService: FontAwesomeService,
    private store: Store<any>,
    private accountService: AccountService,
    private printDocumentsService: PrintDocumentsService,
    private browserPrintService: BrowserPrintService,
    private shipmentService: ShipmentService,
    private paymentService: PaymentService,
    private orderCreationService: OrderCreationService,
    public googleAnalyticsEventsService: GoogleAnalyticsEventsService,
    private router: Router,
    private translate: TranslateService,
    public matSnackBar: MatSnackBar
  ) {}

  ngOnInit() {
    this.store.select(selectUser)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((data: User) => {
        this.user = data;
        if (data) {
          this.userPrintSetting = data.settings.unifaun_print_settings;
          this.userPrintFormat = data.settings.unifaun_print_format;
        }
      });

    this.store.select(selectOrderCompletion)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(data => {
        this.orderCompletion = data;
        if (this.orderCompletion.parameters) {
          this.handleOrder(this.orderCompletion.parameters);
        }
      });

    this.store.select(selectOrderCreation)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((state: OrderCreationState) => {
        this.shipments = Object.keys(state.shipments).map(id => {
          return {id, ...state.shipments[id]};
        });
      });

    this.store.select(selectFooterAction)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(action => {
        let hasWaybill = [];
        switch (action) {
          case FooterActionEnum.GO_TO_BOOKED_SHIPMENTS:
            this.router.navigate(['shipments', 'booked']);
            break;
          case FooterActionEnum.PRINT_ORDER_LABELS:
            if (this.orderId) {
              // if (this.userPrintFormat === PrintFormatEnum.ZPL) {
              //   this.browserPrintService.printFreightDocsByOrderId(this.orderCompletion.data.order.database_id, PrintTypeEnum.LABEL);
              // } else {
                this.printDocumentsService.printFreightDocsByOrderId(this.orderId, PrintTypeEnum.LABEL, this.userPrintFormat, this.userPrintSetting);
              // }
              // this.printDocuments.printOrderLabels();
            }
            break;
          case FooterActionEnum.PRINT_ORDER_WAYBILL:
            if (this.orderId) {
              hasWaybill = this.orderCompletion.data.shipments.map((shipment) => {
                if (environment.Unifaun.waybillProducts.includes(shipment.product_id)) {
                  return shipment.product_id;
                }
              }).filter(Number);

              if (hasWaybill.length === 0) {
                // No waybills
                this.snackBar(this.translate.instant('account.section.shipment.booked.noWaybill'));
              } else {
                this.printDocumentsService.printFreightDocsByOrderId(this.orderId, PrintTypeEnum.WAYBILL);
                // this.printDocuments.printOrderLabels();
              }
            }
            break;
          case FooterActionEnum.PRINT_ORDER_RECEIPT_LIST:
            if (this.orderId) {
              this.printDocumentsService.printFreightDocsByOrderId(this.orderId, PrintTypeEnum.RECEIPT);
              // this.printDocuments.printOrderLabels();
            }
            break;
          case FooterActionEnum.PRINT_ORDER_PICKING_LIST:
            if (this.orderId) {
              this.printDocumentsService.printFreightDocsByOrderId(this.orderId, PrintTypeEnum.PICKING_LIST);
              // this.printDocuments.printOrderLabels();
            }
            break;
          case FooterActionEnum.PRINT_ORDER_RECEIPT:
            if (this.orderId) {
              this.printDocumentsService.printOrderReceipt(this.orderId);
            }
            break;
        }
      });
  }

  handleOrder(parameters) {
    this.shipmentService.createOrder(parameters)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((data) => {
          let isOrderSuccessful: boolean = false;
          if (data.order && data.order.payer_data) {
            if (data.order.payer_data.result_type === 'redirect') {
              isOrderSuccessful = data.order.payer_data.contents.includes('payer-success');
              if (!isOrderSuccessful) {
                const ref = data.order.payer_data.contents.split('/').pop();
                if (ref) {
                  this.paymentService.payerFail(ref)
                    .subscribe();
                }
              }
            } else if (
              data.order.payer_data.result_type === 'error' ||
              data.order.payer_data.result_type === 'html') {
              isOrderSuccessful = false;
            }
          } else if (data.order && !data.order.payer_data) {
            isOrderSuccessful = true;
          }

          if (isOrderSuccessful) {
            this.addTransaction(data);
            this.orderId = data.order.database_id;
            this.store.dispatch(new OrderLoadSuccess({data}));
            this.store.dispatch({type: ORDER_CREATION_CLEAR});
            setTimeout(() => {
              this.router.navigate(['shipments', 'booked']);
            }, 3000);
          } else {
            this.store.dispatch(new OrderLoadFail({order_id: data.order.database_id}));
            this.store.dispatch(new DisableInvoice());
            this.store.dispatch(new ModalDataSet(
              {
                bodyModalText: 'Unfortunately, it is impossible to pay with invoice, please try to use credit card',
                removeShow: false,
                showCancel: false,
                showOk: true
              }));
            this.router.navigate(['create-consignment', 'order-summary']);
          }
        },
        error => {

          const {paymentError, shipmentPaymentStatuses} = this.orderCreationService.handleOrderCreationErrors(error);
          this.store.dispatch(new ModalDataSet(
            {
              bodyModalText: paymentError,
              removeShow: false,
              showCancel: false,
              showOk: true
            }));
          this.shipments.forEach((shipment: Shipment, index: number) => {
            const payment_status: ShipmentPaymentStatusElement = shipmentPaymentStatuses[index];
            if (payment_status) {
              this.store.dispatch(new ShipmentUpdate({shipment: {...shipment, payment_status}}));
            }
          });
          this.store.dispatch(new OrderLoadFail({order_id: null}));
          this.router.navigate(['create-consignment', 'order-summary']);
        });
  }

  addTransaction(data) {
      const transaction = {
          'id': data.order.id,
          'affiliation': 'Freightseeker TA',
          'revenue': data.order.charge + data.order.vat,
          // 'total': data.order.charge + data.order.vat,
          'tax': data.order.vat,
          // 'shipping': '',
          // 'city': '',
          // 'state': '',
          'country': 'Sweden',
          'currency': 'SEK'
      };

      this.googleAnalyticsEventsService.addTransaction(transaction);
      const items = [];
      data.shipments.forEach(shipment => {
          items.push({
              'id': data.order.id,
              'name': shipment.details.name + ' - ' + shipment.details.product,
              'sku': shipment.details.product,
              'category': shipment.details.name,
              'price': shipment.price + shipment.vat,
              'quantity': '1',
              'currency': 'SEK'
          });
          // if (shipment.addons.length > 0) {
          //   shipment.addons.forEach(addon => {
          //     items.push({
          //       'id': data.order.id,
          //       'name': addon.addon.title + ' ' + addon.addon_id,
          //       'sku': addon.addon.service + '-' + addon.addon_id,
          //       'category': addon.addon.title,
          //       'price': addon.price,
          //       'quantity': '1',
          //       'currency': 'SEK'
          //     });
          //   });
          // }
      });

      this.googleAnalyticsEventsService.addItem(items);
  }

  private snackBar(text: string, isError: boolean = true): void {
    this.matSnackBar.openFromComponent(SnackBarComponent, {
      data: {
        error: isError,
        message: text
      },
      panelClass: 'snackbar-container',
      duration: 3000,
    });
  }

  printLabels() {
    //this.printDocuments.printOrderLabels();
  }

  printReceipts() {
    //this.printDocuments.printOrderReceipts();
  }

  ngOnDestroy() {
    //this.printDocuments.unsubscribeUser();
    this.unsubscribe.next();
    this.unsubscribe.complete();
    if (!this.orderCompletion.parameters) {
      this.store.dispatch(new OrderCompletionClear());
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/consignment/order-complete/order-complete.component.ts